// Lib
import React, { useLayoutEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import { useSelector } from 'react-redux';

// Utils
import { getElementId } from '../../../../../common/elements/utils/elementPropertyUtils';

// Hooks
import {
    ImageModalV2Mode,
    isImageModalV2ZoomEnabled,
    shouldHideImageModalV2ViewForEditor,
    shouldShowImageModalV2DrawingOverlay,
    shouldShowImageModalV2TransparencyGrid,
} from './hooks/useImageModalV2Mode';
import { useImageModalShell } from './hooks/useImageModalShell';
import { useImageModalV2PresentedMode } from './hooks/useImageModalV2PresentedMode';

// Components
import ImageModalV2Content from './content/ImageModalV2Content';
import ImageModalV2EditModeContent from './content/ImageModalV2EditModeContent';
import ImageModalV2Toolbar from './toolbar/ImageModalV2Toolbar';
import ImageModalFilesizePreloader from '../ImageModalFilesizePreloader';
import ImageModalV2FileTransferToast from './toolbar/ImageModalV2FileTransferToast';
import ImageModalV2OperationToast from './toolbar/ImageModalV2OperationToast';
import ImageModalV2ErrorToast from './toolbar/ImageModalV2ErrorToast';
import { useTranslator } from '../../../../components/i18n/useTranslator';

import { lowerQualityImageUrlSelector } from '../imageModalSelectors';

// Types
import { ImageModalV2Props } from './imageModalV2Types';
import { UseImageModalZoomResult } from '../hooks/useImageModalZoom';
import { ReduxState } from '../../../../types/reduxTypes';
import type { ImageModalV2OperationToastState } from './toolbar/imageModalV2OperationToastTypes';
import { IMAGE_MODAL_V2_TOAST_EXIT_HANDOFF_HOLD_MS } from './imageModalV2Constants';
import { IMAGE_MODAL_V2_BOTTOM_SURFACE } from './toolbar/imageModalV2BottomSurface';
import { useImageModalV2BottomSurface } from './toolbar/useImageModalV2BottomSurface';
import type { ImageModalV2ErrorToastState } from './toolbar/imageModalV2ErrorToastTypes';

// Styles
import './ImageModalV2.scss';

type ImageAreaContentProps = Pick<ImageModalV2Props, 'element' | 'isEditable'> & {
    mode: ImageModalV2Mode;
    lowerQualityImageUrl?: string;
    contentStyle: React.CSSProperties;
    editOverlayStyle: React.CSSProperties;
    imageWrapperStyle: React.CSSProperties;
    imageBounds: ReturnType<typeof useImageModalShell>['layout']['imageBounds'];
    captionRef: React.RefObject<HTMLDivElement>;
    imageAreaRef: React.RefObject<HTMLDivElement>;
    imageZoom: UseImageModalZoomResult;
    editModeProps: ReturnType<typeof useImageModalShell>['editModeProps'];
    editorIsReady: boolean;
    onEditorReadyChange: (ready: boolean) => void;
    onViewImageLoad: (sourceUrl: string) => void;
    onOperationToastChange: (toast: ImageModalV2OperationToastState | null) => void;
    onErrorToastChange: (toast: ImageModalV2ErrorToastState | null) => void;
};

const ImageAreaContent: React.FC<ImageAreaContentProps> = ({
    element,
    isEditable,
    mode,
    lowerQualityImageUrl,
    contentStyle,
    editOverlayStyle,
    imageWrapperStyle,
    imageBounds,
    captionRef,
    imageAreaRef,
    imageZoom,
    editModeProps,
    editorIsReady,
    onEditorReadyChange,
    onViewImageLoad,
    onOperationToastChange,
    onErrorToastChange,
}) => {
    const showViewModeTransparencyGrid = shouldShowImageModalV2TransparencyGrid(mode);
    const hideViewContentForEdit = shouldHideImageModalV2ViewForEditor(mode, editorIsReady);
    const viewContent = (
        <ImageModalV2Content
            element={element}
            isEditable={isEditable}
            lowerQualityImageUrl={lowerQualityImageUrl}
            showTransparencyGrid={showViewModeTransparencyGrid}
            showDrawing={shouldShowImageModalV2DrawingOverlay(mode)}
            isHiddenForEdit={hideViewContentForEdit}
            isZoomEnabled={isImageModalV2ZoomEnabled(mode)}
            contentStyle={contentStyle}
            imageWrapperStyle={imageWrapperStyle}
            captionRef={captionRef}
            imageAreaRef={imageAreaRef}
            imageZoom={imageZoom}
            onImageLoad={onViewImageLoad}
        />
    );

    const editContent = (
        <ImageModalV2EditModeContent
            mode={mode}
            {...editModeProps}
            contentStyle={contentStyle}
            editOverlayStyle={editOverlayStyle}
            imageWrapperStyle={imageWrapperStyle}
            imageBounds={imageBounds}
            onEditorReadyChange={onEditorReadyChange}
            onOperationToastChange={onOperationToastChange}
            onErrorToastChange={onErrorToastChange}
        />
    );

    switch (mode) {
        case ImageModalV2Mode.VIEW:
        case ImageModalV2Mode.UPLOAD:
        case ImageModalV2Mode.DOWNLOAD:
            return viewContent;
        case ImageModalV2Mode.DRAW:
        case ImageModalV2Mode.CROP:
        case ImageModalV2Mode.BACKGROUND:
            return (
                <>
                    {viewContent}
                    {editContent}
                </>
            );
    }
};

const ImageModalV2: React.FC<ImageModalV2Props> = (props) => {
    const { element, isEditable } = props;
    const elementId = getElementId(element);
    const lowerQualityImageUrl = useSelector((state: ReduxState) => lowerQualityImageUrlSelector(state, props));
    const uploadTranslate = useTranslator('popup', 'imageModal.upload');
    const [editorIsReady, setEditorIsReady] = useState(false);

    const {
        modalRef,
        primaryToolbarRef,
        captionRef,
        mode,
        setMode,
        isToolPanelOpen,
        layout,
        imageZoom,
        editModeProps,
        canNavigate,
        orderedImageElements,
        navigateToPreviousImage,
        navigateToNextImage,
        handleModalMouseDown,
        handleModalClick,
        handleViewImageLoaded,
        runPendingCroppedImageCreation,
    } = useImageModalShell(props);
    const { presentedMode, completeSecondaryToolbarExit } = useImageModalV2PresentedMode({ elementId, mode });
    const {
        openSurface,
        completeExit,
        fileTransferSurface,
        operationSurface,
        presentedModalToast,
        isModalToastOpen,
        isSavingChangesToast,
        handleModalToastExitComplete,
        handleOperationToastChange,
        handleErrorToastChange,
        onFileTransferSurfaceExited,
    } = useImageModalV2BottomSurface({ elementId, mode, isToolPanelOpen });

    const isToolbarVisible = !imageZoom.isZoomed;
    const imageAreaRef = useRef<HTMLDivElement>(null);

    useLayoutEffect(() => {
        if (mode === ImageModalV2Mode.CROP || mode === ImageModalV2Mode.BACKGROUND) setEditorIsReady(false);
    }, [elementId, mode]);

    return (
        <>
            <ImageModalFilesizePreloader imagesToPreload={orderedImageElements} />
            {fileTransferSurface.shouldMount && (
                <ImageModalV2FileTransferToast
                    elementId={elementId}
                    uploadLoadingLabel={uploadTranslate('uploadingImage')}
                    isOpen={fileTransferSurface.isOpen}
                    onExitComplete={onFileTransferSurfaceExited}
                />
            )}
            {operationSurface.shouldMount && presentedModalToast?.type === 'operation' && (
                <ImageModalV2OperationToast
                    toast={presentedModalToast.toast}
                    isOpen={isModalToastOpen}
                    exitHandoffMs={isSavingChangesToast ? undefined : IMAGE_MODAL_V2_TOAST_EXIT_HANDOFF_HOLD_MS}
                    onExitComplete={handleModalToastExitComplete}
                    onEnterComplete={isSavingChangesToast ? runPendingCroppedImageCreation : undefined}
                />
            )}
            {operationSurface.shouldMount && presentedModalToast?.type === 'error' && (
                <ImageModalV2ErrorToast
                    toast={presentedModalToast.toast}
                    isOpen={isModalToastOpen}
                    onExitComplete={handleModalToastExitComplete}
                />
            )}

            <div
                ref={modalRef}
                className={classNames(
                    'ImageModalV2',
                    `mode-${presentedMode.toLowerCase()}`,
                    'fixed inset-0 box-border flex flex-col pointer-events-auto',
                )}
                onMouseDownCapture={handleModalMouseDown}
                onClick={handleModalClick}
            >
                <div
                    ref={imageAreaRef}
                    className="ImageModalV2-image-area relative flex w-full min-h-0 flex-none items-center justify-center overflow-hidden"
                    style={layout.imageAreaStyle}
                >
                    <ImageAreaContent
                        element={element}
                        isEditable={isEditable}
                        mode={presentedMode}
                        lowerQualityImageUrl={lowerQualityImageUrl}
                        contentStyle={layout.contentStyle}
                        editOverlayStyle={layout.editOverlayStyle}
                        imageWrapperStyle={layout.imageWrapperStyle}
                        imageBounds={layout.imageBounds}
                        captionRef={captionRef}
                        imageAreaRef={imageAreaRef}
                        imageZoom={imageZoom}
                        editModeProps={editModeProps}
                        editorIsReady={editorIsReady}
                        onEditorReadyChange={setEditorIsReady}
                        onViewImageLoad={handleViewImageLoaded}
                        onOperationToastChange={handleOperationToastChange}
                        onErrorToastChange={handleErrorToastChange}
                    />
                </div>

                <ImageModalV2Toolbar
                    mode={mode}
                    element={element}
                    isEditable={isEditable}
                    canNavigate={canNavigate}
                    navigateToPreviousImage={navigateToPreviousImage}
                    navigateToNextImage={navigateToNextImage}
                    setMode={setMode}
                    bottomSurface={openSurface}
                    isVisible={isToolbarVisible}
                    style={layout.toolbarStyle}
                    primaryToolbarRef={primaryToolbarRef}
                    onPrimaryToolbarExitComplete={() => completeExit(IMAGE_MODAL_V2_BOTTOM_SURFACE.PRIMARY_TOOLBAR)}
                    onSecondaryToolbarExitComplete={() => {
                        completeSecondaryToolbarExit();
                        completeExit(IMAGE_MODAL_V2_BOTTOM_SURFACE.SECONDARY_TOOLBAR);
                    }}
                />
            </div>
        </>
    );
};

export default ImageModalV2;
